import { Options } from 'humanize-duration';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { AppConfig } from '@core/utils/config';
import { R } from '@core/utils/r';
import { Repository } from '@core/utils/repository';
import { memoize, memoizeClearKey } from '@core/utils/rxjs/operators';
import { Session } from '@modules/auth/session';
import { of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { UserResponse, UserId, UserRole } from '@business/entities/account';
import { UserAPI } from '@business/api/user';

export class UserBloc extends BaseBloc {
  private readonly usersRepo = new Repository<UserResponse>({
    getItemId: user => user.id,
  });

  readonly usersWithUnassigned$ = this.usersRepo.items$.pipe(map(users => R.sortBy(users, u => u.firstName)));
  readonly users$ = this.usersWithUnassigned$.pipe(map(users => R.filter(users, u => u.id !== '9999' && u.firstName !== 'Unassigned')));

  readonly users = this.usersRepo.items;
  readonly allUsers$ = this.usersRepo.items$;
  readonly staff$ = this.usersRepo.items$.pipe(
    map(users =>
      users.filter(u => u.role === UserRole.admin || u.role === UserRole.user || u.role == UserRole.affiliate || u.role === UserRole.noLogin),
    ),
    map(users => R.sortBy(users, u => u.firstName)),
  );

  readonly onlyAdmins$ = this.usersRepo.items$.pipe(
    map(users => users.filter(u => u.role === UserRole.admin)),
    map(users => R.sortBy(users, u => u.firstName)),
  );

  readonly clients$ = this.usersRepo.items$.pipe(map(users => users.filter(u => u.role === UserRole.client)));
  readonly selectUser = this.usersRepo.selectItem;

  onReset() {
    this.usersRepo.reset();
  }

  fetchUser = (id: UserId) => {
    return UserAPI.fetchUser(id).pipe(
      this.usersRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_users_error') }),
    );
  };

  fetchUsers = () => {
    return UserAPI.fetchUsers().pipe(
      this.usersRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_users_error') }),
      memoize({
        key: 'fetchUsers',
        params: { appId: AppConfig.current().appId, companyId: AppConfig.current().companyId },
      }),
    );
  };

  createUser = (user: Partial<UserResponse>) => {
    return UserAPI.createUser(user).pipe(
      this.usersRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_user'),
      }),
      memoizeClearKey({
        key: 'fetchUsers',
        params: { appId: AppConfig.current().appId, companyId: AppConfig.current().companyId },
      }),
    );
  };

  updateUser = (id: UserId, user: Partial<UserResponse>) => {
    return UserAPI.updateUser(id, user).pipe(
      this.usersRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_user'),
      }),
      memoizeClearKey({
        key: 'fetchUsers',
        params: { appId: AppConfig.current().appId, companyId: AppConfig.current().companyId },
      }),
    );
  };

  changePassword = (id: UserId, password: string) => {
    return UserAPI.changePassword(id, password).pipe(
      this.usersRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_user'),
      }),
    );
  };

  uploadImage = (id: UserId, data: File) => {
    return UserAPI.uploadImage(id, data).pipe(
      this.usersRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_user'),
      }),
      memoizeClearKey({
        key: 'fetchUsers',
        params: { appId: AppConfig.current().appId, companyId: AppConfig.current().companyId },
      }),
    );
  };
}
